import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs/Rx';
import { ApplicationService } from 'app/shared/services/app/application/application.service';
import { UserService } from 'app/shared/services/user/user.service';
import { GlobalUser } from 'app/shared/services/user/global-user.model';
import { GlobalRole } from 'app/shared/services/user/base-user.model';
import { ImageUploadService } from 'app/shared/services/image-upload/image-upload.service';
import { Application } from 'app/shared/services/app/application/application.model';

@Injectable()
export class AppBuilderService {
  private _forms: any = {
    general: {
      valid: false,
      value: {},
      dirty: false
    },
    mobile: {
      valid: false,
      value: {},
      dirty: false
    },
    features: {
      dirty: false
    },
    admins: {
      valid: false,
      assignedAdmins: [],
      dirty: false
    },
    aws: {
      valid: false,
      value: {},
      dirty: false
    },
    push: {
      valid: false,
      value: {},
      dirty: false
    }
  };

  private _resets: any = {
    aws: new Subject<any>(),
    push: new Subject<any>()
  };

  defaultFeatures: any[] = [];

  private _allAdmins: GlobalUser[] = [];
  private _availableAdmins: GlobalUser[] = [];

  private _includePush: boolean = false;

  private _skipUserAssignment: boolean = false;

  constructor(private _appService: ApplicationService,
              private _userService: UserService,
              private _ius: ImageUploadService) {
    this.defaultFeatures = this._appService.defaultFeatures;
    if (this._userService) this._userService.getAllGlobalUsers()
    .then((users: GlobalUser[]) => {
      this._allAdmins = users.filter(user => user.globalRole.level == GlobalRole.Admin);
      this.updateAvailableAdmins();
    });
  }

  getFormValidity(key: string) {
    return this._forms[key].valid;
  }

  getFormDirty(key: string) {
    return this._forms[key].dirty;
  }

  markFormAsClean(key: string) {
    this._forms[key].dirty = false;
  }

  markFormAsDirty(key: string) {
    this._forms[key].dirty = true;
  }

  getReset(key: string) {
    return this._resets[key];
  }

  resetAndMarkClean(key: string) {
    this.getReset(key).next();
    this.markFormAsClean(key);
  }

  get assignedAdmins() {
    return this._forms.admins.assignedAdmins;
  }

  get selectedFeatures(): any[] {
    return this.defaultFeatures.filter(feature => feature.selected);
  }

  get featureFormValid() {
    return this.selectedFeatures.length >= 1;
  }

  get includePush() {
    return this._includePush;
  }

  get availableAdmins() {
    return this._availableAdmins;
  }

  get userFormValid() {
    return this._forms.admins.assignedAdmins.length >= 1;
  }

  // isEdit is only for checking the aws and push forms,
  // since they have some tricky logic in edit mode
  updateForm(form: FormGroup, key: string, isEdit?: boolean) {
    this._forms[key].value = form.value;

    if (isEdit && (key == 'aws' || key == 'push')) {
      this.checkAwsOrPush(form.value, key);
    }

    this._forms[key].valid = form.valid;
    this._forms[key].dirty = form.dirty;
  }

  checkAwsOrPush(form: any, formName: string) {
    let allEmpty: boolean = true;

    for (let key in form) {
      if (form.hasOwnProperty(key)) {
        if (form[key]) {
          allEmpty = false;
          break;
        }
      }
    }

    if (allEmpty) this.resetAndMarkClean(formName);
  }

  // for aws/push settings edit screen
  initAWSPush(appFeatures: any[]) {
    appFeatures.forEach((feature) => {
      if (feature.display_name === 'Push Notifications') {
        this._includePush = true;
      }
    });
  }

  uploadNewImage(e: any, appName: string) {
    return this._ius.uploadCroppedImage(appName, e.file)
    .then((imageUrl) => {
      return imageUrl;
    });
  }

  initializeFeatures(app: Application) {
    this.defaultFeatures.filter((defaultFeature) => {
      defaultFeature.selected = app.hasFeature(defaultFeature.system_name);
      return app.hasFeature(defaultFeature.system_name);
    });
  }

  toggleFeature(sysName: any) {
    if (sysName == 'push-notifications') this.changePush();
    this._forms.features.dirty = true;
  }

  removeFeature(feature: any) {
    feature.selected = false;
    if (feature.sysNames == 'push-notifications') this.changePush(true);
    this._forms.features.dirty = true;
  }

  changePush(forceDisable?: boolean) {
    if (this._includePush || forceDisable) {
      this._includePush = false;
    } else {
      this._includePush = true;
    }
  }

  updateAvailableAdmins() {
    let newManagerArray = this._allAdmins.filter((user) => {
      for (let i = 0; i < this._forms['admins'].assignedAdmins.length; i++) {
        if (this._forms['admins'].assignedAdmins[i] === user) {
          return false;
        }
      }
      return true;
    });
    this._availableAdmins = newManagerArray;
  }

  assignUser(user: any, skipUserForm?: boolean) {
    if (skipUserForm) this._skipUserAssignment = true;
    if (!user) return;
    // prevents the same app maanger from being added multiple times
    for (let i = 0; i < this._forms['admins'].assignedAdmins.length; i++) {
      if (this._forms['admins'].assignedAdmins[i] === user) {
        return;
      }
    }
    this._forms['admins'].assignedAdmins.push(user);
    this.updateAvailableAdmins();
  }

  removeUser(user: GlobalUser) {
    if (!user) return;
    this._forms['admins'].assignedAdmins = this._forms['admins'].assignedAdmins.filter(each => {
      return each !== user;
    });
    this.updateAvailableAdmins();
  }

  // specific to application-create
  get generalPhaseValid() {
    return this.getFormValidity('general') && this.getFormValidity('mobile');
  }

  get skipUserAssignment() {
    return this._skipUserAssignment;
  }

  get createFormInvalid() {
    return !this.generalPhaseValid ||
           !this.featureFormValid ||
           !this._forms.aws.valid ||
           (!this._forms.push.valid && this._includePush);
  }

  // we could _probably_ just check general and mobile, but...
  get createAppFormTouched() {
    return this._forms['general'].dirty ||
           this._forms['mobile'].dirty ||
           this._forms['features'].dirty ||
           this._forms['admins'].dirty ||
           this._forms['aws'].dirty ||
           this._forms['push'].dirty;
  }

  get createApplicationSaveObject() {
    let applicationSettings: any = this.generalSettingsSaveObject;

    applicationSettings.permissions = this.appFeaturesSaveObject;

    applicationSettings.aws = this.awsAndOrPushSaveObject;

    return applicationSettings;
  }

  get appFeaturesSaveObject() {
    return this.selectedFeatures.map(feature => feature.system_name);
  }

  get mobileLoginSettingsSaveObject() {
    return this._forms['mobile'].value;
  }

  get generalSettingsSaveObject() {
    let applicationSettings: any = {};

    applicationSettings.sys_name = this._forms.general.value.sys_name;
    applicationSettings.logo = this._forms.general.value.logo;

    applicationSettings.settings = {
      app_name: this._forms.general.value.app_name,
      descr: this._forms.general.value.descr,
      version: this._forms.general.value.version,
      package: this._forms.general.value.package
    };

    return applicationSettings;
  }

  get awsAndOrPushSaveObject() {
    let saveObj: any = {};

    if (this._forms.aws.dirty && this._forms.aws.valid) {
      saveObj = this._forms.aws.value;
    }

    if (this._includePush && this._forms.push.dirty && this._forms.push.valid) {
      let pushValue = this._forms.push.value;

      for (let key in pushValue) {
        if (pushValue.hasOwnProperty(key)) {
          saveObj[key] = pushValue[key];
        }
      }
    }

    return saveObj;
  }

}



// WEBPACK FOOTER //
// /app/src/app/forms/services/app-builder/app-builder.service.ts